import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PlusIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import stockService, { PeseeComplete } from '../services/stockService';
import productsService from '../services/productsService';
import StockModal from '../components/modals/StockModal';
import { PeseeModificationModal } from '../components/modals';
import PeseeDetailsModal from '../components/PeseeDetailsModal';
import { usePermissions } from '@/hooks/usePermissions';
import { PLATFORM_ROLES, OPERATIONAL_ROLES } from '@/types/permissions';

const StocksPage: React.FC = () => {
  const { hasAnyRole } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [peseesCompletes, setPeseesCompletes] = useState<PeseeComplete[]>([]);
  const [selectedPesees, setSelectedPesees] = useState<PeseeComplete[]>([]);
  const [showStockForm, setShowStockForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // États pour les produits
  const [availableProducts, setAvailableProducts] = useState<Array<{value: string, label: string}>>([]);
  
  // Vérifier si l'utilisateur peut modifier les pesées
  const canModifyPesees = hasAnyRole([
    PLATFORM_ROLES.PLATFORM_SUPER_ADMIN,
    OPERATIONAL_ROLES.SITE_SUPERVISOR,
    OPERATIONAL_ROLES.SITE_MANAGER
  ]);
  
  // États pour la modification et visualisation des détails
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPeseeForModification, setSelectedPeseeForModification] = useState<PeseeComplete | null>(null);
  const [selectedPeseeForDetails, setSelectedPeseeForDetails] = useState<PeseeComplete | null>(null);

  // Types de pesée
  const peseeTypes = [
    { value: 'reception', label: 'Réception' },
    { value: 'expedition', label: 'Expédition' },
    { value: 'transfert', label: 'Transfert' }
  ];

  // Charger les pesées complètes
  const loadPeseesCompletes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await stockService.getPeseesCompletes();
      setPeseesCompletes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des pesées complètes:', error);
      setError('Erreur lors du chargement des pesées complètes');
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadPeseesCompletes();
    loadProducts();
  }, []);

  // Charger les produits depuis la table des produits
  const loadProducts = async () => {
    try {
      const products = await productsService.getProducts();
      setAvailableProducts(products);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setError('Erreur lors du chargement des produits');
    }
  };


  // Réinitialiser la pagination lors des changements de filtres
  useEffect(() => {
    setCurrentPage(1);
    setSelectedPesees([]);
  }, [searchTerm, selectedType, selectedProduct, dateFrom, dateTo]);

  // Filtrer les pesées selon les critères
  const filteredPesees = peseesCompletes.filter(pesee => {
    // Exclure les pesées déjà en stock (vérifier explicitement true)
    const enStock = (pesee as any).en_stock;
    if (enStock === true || enStock === 'true' || enStock === 1) {
      return false;
    }
    
    const matchesSearch = !searchTerm || 
      pesee.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pesee.produit?.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pesee.camion?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || pesee.type_pesee === selectedType;
    const matchesProduct = selectedProduct === 'all' || 
      (pesee.produit?.id && pesee.produit.id.toString() === selectedProduct) ||
      ((pesee as any).produit_info?.id && (pesee as any).produit_info.id.toString() === selectedProduct);
    
    // Filtre par période
    const matchesDateRange = (() => {
      if (!dateFrom && !dateTo) return true;
      
      const peseeDate = new Date(pesee.date_heure);
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null; // Inclure toute la journée de fin
      
      if (fromDate && toDate) {
        return peseeDate >= fromDate && peseeDate <= toDate;
      } else if (fromDate) {
        return peseeDate >= fromDate;
      } else if (toDate) {
        return peseeDate <= toDate;
      }
      return true;
    })();
    
    return matchesSearch && matchesType && matchesProduct && matchesDateRange;
  });

  // Calculer la pagination
  const totalCount = filteredPesees.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPesees = filteredPesees.slice(startIndex, endIndex);

  // Fonction pour changer de page
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setSelectedPesees([]); // Réinitialiser la sélection lors du changement de page
  };

  // Gérer la sélection des pesées
  const handlePeseeSelect = (pesee: PeseeComplete, isSelected: boolean) => {
    if (isSelected) {
      setSelectedPesees(prev => [...prev, pesee]);
    } else {
      setSelectedPesees(prev => prev.filter(p => p.id !== pesee.id));
    }
  };

  // Gérer la sélection de toutes les pesées
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedPesees(paginatedPesees);
    } else {
      setSelectedPesees([]);
    }
  };

  // Gérer l'ouverture du modal de modification
  const handleModifyPesee = (pesee: PeseeComplete) => {
    setSelectedPeseeForModification(pesee);
    setShowModifyModal(true);
  };

  // Gérer l'ouverture du modal de détails
  const handleViewDetails = (pesee: PeseeComplete) => {
    setSelectedPeseeForDetails(pesee);
    setShowDetailsModal(true);
  };

  // Fermer les modals
  const handleCloseModifyModal = () => {
    setShowModifyModal(false);
    setSelectedPeseeForModification(null);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedPeseeForDetails(null);
  };

  // Gérer le succès de la modification
  const handleModificationSuccess = (message: string) => {
    setSuccessMessage(message);
    loadPeseesCompletes(); // Recharger la liste
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <Link to="/dashboard" className="p-2 text-gray-400 hover:text-gray-600 mr-4">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Stocks</h1>
          <p className="text-gray-600">Mettre en stock les pesées complètes</p>
        </div>
      </div>

      {/* Messages d'erreur et de succès */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckIcon className="h-5 w-5 text-green-400 mr-2" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Recherche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Référence, produit, véhicule..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          {/* Type de pesée */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de pesée
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">Tous les types</option>
              {peseeTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Produit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produit
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">Tous les produits ({availableProducts.length})</option>
              {availableProducts.map((product) => (
                <option key={product.value} value={product.value}>
                  {product.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date de début */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Date de fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
        
        {/* Indicateur de filtres actifs */}
        {(searchTerm || selectedType !== 'all' || selectedProduct !== 'all' || dateFrom || dateTo) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-600">
                <i className="fas fa-filter text-blue-500 mr-2"></i>
                <span>Filtres actifs:</span>
                <div className="ml-2 flex flex-wrap gap-2">
                  {searchTerm && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      Recherche: {searchTerm}
                    </span>
                  )}
                  {selectedType !== 'all' && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                      Type: {peseeTypes.find(t => t.value === selectedType)?.label}
                    </span>
                  )}
                  {selectedProduct !== 'all' && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                      Produit: {availableProducts.find(p => p.value === selectedProduct)?.label}
                    </span>
                  )}
                  {dateFrom && dateTo && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                      Période: {dateFrom} - {dateTo}
                    </span>
                  )}
                  {dateFrom && !dateTo && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                      Depuis: {dateFrom}
                    </span>
                  )}
                  {!dateFrom && dateTo && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                      Jusqu'au: {dateTo}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedType('all');
                  setSelectedProduct('all');
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-xs text-red-600 hover:text-red-800"
              >
                <i className="fas fa-times mr-1"></i>Effacer tous les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Contenu principal */}
      <div className="bg-white rounded-lg shadow">
        {/* En-tête avec actions */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Pesées complètes ({totalCount})
              </h3>
              <p className="text-sm text-gray-500">
                {selectedPesees.length > 0 && `${selectedPesees.length} sélectionnée(s)`}
                {totalPages > 1 && ` • Page ${currentPage} sur ${totalPages}`}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {selectedPesees.length > 0 && (
                <button
                  onClick={() => setShowStockForm(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Mettre en stock ({selectedPesees.length})
                </button>
              )}
              <button
                onClick={() => loadPeseesCompletes()}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                {isLoading ? 'Chargement...' : 'Actualiser'}
              </button>
            </div>
          </div>
        </div>

        {/* Tableau des pesées */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={paginatedPesees.length > 0 && selectedPesees.length === paginatedPesees.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Référence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Véhicule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Poids net (kg)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mr-2"></div>
                      Chargement des pesées...
                    </div>
                  </td>
                </tr>
              ) : paginatedPesees.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    {peseesCompletes.length === 0 
                      ? 'Aucune pesée complète trouvée'
                      : 'Aucune pesée ne correspond aux critères de recherche'
                    }
                  </td>
                </tr>
              ) : (
                paginatedPesees.map((pesee) => (
                  <tr key={pesee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPesees.some(p => p.id === pesee.id)}
                        onChange={(e) => handlePeseeSelect(pesee, e.target.checked)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{pesee.reference}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(pesee.date_heure).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(pesee.date_heure).toLocaleTimeString('fr-FR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pesee.type_pesee === 'reception' ? 'bg-green-100 text-green-800' :
                        pesee.type_pesee === 'expedition' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {peseeTypes.find(t => t.value === pesee.type_pesee)?.label || pesee.type_pesee}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{pesee.produit?.nom || (pesee as any).produit_nom || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(() => {
                          const peseeAny = pesee as any;
                          const immatriculation = peseeAny.camion_immatriculation || pesee.camion || '';
                          const marque = peseeAny.camion_marque || '';
                          if (immatriculation && marque) {
                            return `${immatriculation} - ${marque}`;
                          }
                          return immatriculation || marque || '';
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pesee.poids_net ? pesee.poids_net.toLocaleString('fr-FR') : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pesee.statut === 'COMPLETEE' ? 'bg-green-100 text-green-800' :
                        pesee.statut === 'TERMINE' ? 'bg-blue-100 text-blue-800' :
                        pesee.statut === 'ARCHIVE' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {pesee.statut}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(pesee)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleModifyPesee(pesee)}
                          disabled={!canModifyPesees}
                          className={`p-1 transition-colors ${
                            canModifyPesees 
                              ? 'text-gray-400 hover:text-orange-600' 
                              : 'text-gray-200 cursor-not-allowed'
                          }`}
                          title={canModifyPesees ? "Modifier la pesée" : "Permissions insuffisantes"}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              {/* Informations de pagination */}
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, totalCount)} sur {totalCount} pesée{totalCount > 1 ? 's' : ''}
                </span>
                
                {/* Sélecteur de taille de page */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Afficher:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newPageSize = parseInt(e.target.value);
                      setPageSize(newPageSize);
                      setCurrentPage(1);
                      setSelectedPesees([]);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-600">par page</span>
                </div>
              </div>

              {/* Navigation de pagination */}
              <div className="flex items-center space-x-2">
                {/* Bouton Première page */}
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Première page"
                >
                  <i className="fas fa-angle-double-left"></i>
                </button>

                {/* Bouton Page précédente */}
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Page précédente"
                >
                  <i className="fas fa-angle-left"></i>
                </button>

                {/* Numéros de pages */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Ajuster si on est proche de la fin
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    // Page 1 avec ellipsis si nécessaire
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                    }

                    // Pages visibles
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`px-3 py-2 border rounded-lg text-sm transition-colors ${
                            i === currentPage
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Ellipsis et dernière page si nécessaire
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                {/* Bouton Page suivante */}
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Page suivante"
                >
                  <i className="fas fa-angle-right"></i>
                </button>

                {/* Bouton Dernière page */}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Dernière page"
                >
                  <i className="fas fa-angle-double-right"></i>
                </button>

                {/* Navigation rapide */}
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
                  <span className="text-sm text-gray-600">Aller à:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        handlePageChange(page);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt((e.target as HTMLInputElement).value);
                        if (page >= 1 && page <= totalPages) {
                          handlePageChange(page);
                        }
                      }
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-600">sur {totalPages}</span>
                </div>
              </div>
            </div>

            {/* Indicateur de progression */}
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(currentPage / totalPages) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de mise en stock */}
      {showStockForm && (
        <StockModal
          isOpen={showStockForm}
          selectedPesees={selectedPesees}
          onClose={() => setShowStockForm(false)}
          onSuccess={(message) => {
            setSuccessMessage(message);
            setShowStockForm(false);
            
            // Retirer immédiatement les pesées mises en stock de la liste locale
            const selectedIds = new Set(selectedPesees.map(p => p.id));
            setPeseesCompletes(prev => 
              prev.map(pesee => {
                if (selectedIds.has(pesee.id)) {
                  // Marquer comme en stock immédiatement
                  return { ...pesee, en_stock: true } as PeseeComplete;
                }
                return pesee;
              }).filter(pesee => {
                // Retirer de la liste si en stock
                const enStock = (pesee as any).en_stock;
                return !(enStock === true || enStock === 'true' || enStock === 1);
              })
            );
            
            // Vider la sélection
            setSelectedPesees([]);
            
            // Recharger depuis le serveur pour s'assurer de la cohérence
            loadPeseesCompletes();
          }}
        />
      )}

      {/* Modal de visualisation des détails */}
      <PeseeDetailsModal
        isOpen={showDetailsModal}
        pesee={selectedPeseeForDetails}
        onClose={handleCloseDetailsModal}
        title={selectedPeseeForDetails ? `Détails de la pesée ${selectedPeseeForDetails.reference}` : undefined}
      />

      {/* Modal de modification */}
        <PeseeModificationModal
          isOpen={showModifyModal}
          pesee={selectedPeseeForModification as any}
          onClose={handleCloseModifyModal}
          onSuccess={handleModificationSuccess}
        />
    </div>
  );
};

export default StocksPage;